import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, LogOut } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

export interface MobileNavItem {
  label: string;
  path: string;
  icon?: ReactNode;
}

interface InfraSection {
  items: MobileNavItem[];
}

interface MobileNavDrawerProps {
  onClose: () => void;
  user: { name?: string | null; email: string; image?: string | null };
  navItems: MobileNavItem[];
  globalNavItems?: MobileNavItem[];
  currentPath: string;
  onNavigate: (path: string) => void;
  onSignOut: () => void;
  projectName?: string;
  infraSection?: InfraSection;
  /** Rendered below Infrastructure in both default and global panels */
  projectListSection?: ReactNode;
  showGlobalNav?: boolean;
  onToggleGlobalNav?: () => void;
}

function isNavItemActive(path: string, pathname: string): boolean {
  if (path === '/dashboard') {
    return pathname === '/dashboard';
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function MobileNavDrawer({
  onClose,
  user,
  navItems,
  globalNavItems,
  currentPath,
  onNavigate,
  onSignOut,
  projectName,
  infraSection,
  projectListSection,
  showGlobalNav,
  onToggleGlobalNav,
}: MobileNavDrawerProps) {
  const [infraOpen, setInfraOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    window.setTimeout(onClose, 250);
  }, [isClosing, onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  // Determine if we're in project context with toggle capability
  const canToggle = Boolean(projectName && globalNavItems && onToggleGlobalNav);

  // Items to display in project view (without the old "Back to Projects" link)
  const projectItems = canToggle
    ? navItems.filter((item) => item.label !== 'Back to Projects')
    : navItems;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        role="presentation"
        data-testid="mobile-nav-backdrop"
        onClick={handleClose}
        className="sam-glass-drawer-backdrop fixed inset-0 glass-backdrop-dim border-0 z-drawer-backdrop"
        data-state={isClosing ? 'closing' : 'open'}
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        data-testid="mobile-nav-panel"
        className="sam-glass-drawer-panel glass-panel-container fixed top-0 right-0 bottom-0 w-[85vw] max-w-80 glass-modal border-r-0 rounded-l-[20px] rounded-r-none z-drawer flex flex-col overflow-hidden before:content-[''] before:absolute before:top-0 before:bottom-0 before:left-0 before:w-[3px] before:bg-[linear-gradient(to_bottom,transparent_0%,rgba(34,197,94,0.55)_50%,transparent_100%)] before:pointer-events-none before:blur-[1px]"
        data-state={isClosing ? 'closing' : 'open'}
      >
        {/* Header: user info + close */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border-default">
          {user.image ? (
            <img
              src={user.image}
              alt={user.name || user.email}
              className="h-9 w-9 rounded-full shrink-0"
            />
          ) : (
            <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center text-fg-on-accent text-sm font-medium shrink-0">
              {(user.name || user.email).charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-fg-primary m-0 overflow-hidden text-ellipsis whitespace-nowrap">
              {user.name || 'User'}
            </p>
            <p className="text-xs text-fg-muted m-0 overflow-hidden text-ellipsis whitespace-nowrap">
              {user.email}
            </p>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close navigation"
            className={`flex items-center justify-center w-10 h-10 bg-transparent border-none text-fg-muted cursor-pointer shrink-0 rounded-sm ${FOCUS_RING}`}
          >
            <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toggle header: Back to Projects / Back to Project Name */}
        {canToggle && (
          <button
              onClick={onToggleGlobalNav}
              data-testid="mobile-nav-toggle"
              className={`flex items-center gap-3 w-full px-5 py-3 bg-transparent border-none border-b border-border-default cursor-pointer text-left text-sm font-medium text-fg-muted hover:text-fg-primary hover:bg-[rgba(34,197,94,0.04)] transition-all duration-150 ${FOCUS_RING}`}
            aria-label={showGlobalNav ? `Back to ${projectName} navigation` : 'Show global navigation'}
          >
            {showGlobalNav ? (
              <>
                <ArrowRight size={16} className="shrink-0" />
                <span className="truncate">Back to {projectName}</span>
              </>
            ) : (
              <>
                <ArrowLeft size={16} className="shrink-0" />
                <span>Back to Projects</span>
              </>
            )}
          </button>
        )}

        {/* Project name header when in project context and showing project nav */}
        {projectName && !showGlobalNav && !canToggle && (
          <div className="px-5 py-2 text-xs font-semibold text-fg-muted uppercase tracking-wider truncate border-b border-border-default" title={projectName}>
            {projectName}
          </div>
        )}

        {/* Project name under toggle button when showing project nav */}
        {canToggle && !showGlobalNav && (
          <div className="px-5 py-2 text-xs font-semibold text-fg-muted uppercase tracking-wider truncate border-b border-border-default" title={projectName}>
            {projectName}
          </div>
        )}

        {/* Nav items with slide transition */}
        <nav
          aria-label={showGlobalNav ? 'Primary navigation' : 'Project navigation'}
          className="flex-1 overflow-hidden relative"
        >
          <div
            className="flex transition-transform duration-200 ease-out motion-reduce:transition-none h-full"
            style={{ transform: canToggle && showGlobalNav ? 'translateX(-100%)' : 'translateX(0)' }}
          >
            {/* Panel 1: Project / default nav items */}
            <div
              className="w-full shrink-0 pt-2 overflow-y-auto"
              aria-hidden={(canToggle && showGlobalNav) || undefined}
              inert={canToggle && showGlobalNav ? true : undefined}
            >
              {(canToggle ? projectItems : navItems).map((item) => {
                const active = isNavItemActive(item.path, currentPath);
                return (
                  <button
                    key={item.path}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-3 w-full min-h-11 px-5 py-2.5 text-base font-medium bg-transparent border-none cursor-pointer text-left border-l-3 transition-all duration-[120ms] ${FOCUS_RING} ${
                      active
                        ? 'text-accent border-l-accent bg-[rgba(34,197,94,0.08)]'
                        : 'text-fg-muted border-l-transparent hover:text-fg-primary hover:bg-[rgba(34,197,94,0.04)]'
                    }`}
                    onClick={() => onNavigate(item.path)}
                  >
                    {item.icon && <span className="shrink-0">{item.icon}</span>}
                    {item.label}
                  </button>
                );
              })}

              {/* Infrastructure section — only in non-toggle mode */}
              {!canToggle && infraSection && (
                <div className="mt-2">
                  <button
                    onClick={() => setInfraOpen(!infraOpen)}
                    className={`flex items-center gap-2 w-full px-5 py-2.5 bg-transparent border-none text-xs font-semibold text-fg-muted uppercase tracking-wider cursor-pointer hover:text-fg-primary hover:bg-[rgba(34,197,94,0.04)] transition-all duration-[120ms] ${FOCUS_RING}`}
                    aria-expanded={infraOpen}
                    aria-controls="mobile-infra-nav-panel"
                  >
                    {infraOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Infrastructure
                  </button>
                  {infraOpen && (
                    <div id="mobile-infra-nav-panel">
                      {infraSection.items.map((item) => {
                        const active = isNavItemActive(item.path, currentPath);
                        return (
                          <button
                            key={item.path}
                            aria-current={active ? 'page' : undefined}
                            className={`flex items-center gap-3 w-full min-h-11 px-5 pl-8 py-2.5 text-base font-medium bg-transparent border-none cursor-pointer text-left border-l-3 transition-all duration-[120ms] ${FOCUS_RING} ${
                              active
                                ? 'text-accent border-l-accent bg-[rgba(34,197,94,0.08)]'
                                : 'text-fg-muted border-l-transparent hover:text-fg-primary hover:bg-[rgba(34,197,94,0.04)]'
                            }`}
                            onClick={() => onNavigate(item.path)}
                          >
                            {item.icon && <span className="shrink-0">{item.icon}</span>}
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Project list — only in non-toggle mode (global nav) */}
              {!canToggle && projectListSection}
            </div>

            {/* Panel 2: Global nav items (only rendered when toggle is available) */}
            {canToggle && (
              <div
                className="w-full shrink-0 pt-2 overflow-y-auto"
                aria-hidden={!showGlobalNav || undefined}
                inert={!showGlobalNav ? true : undefined}
              >
                {(globalNavItems ?? []).map((item) => {
                  const active = isNavItemActive(item.path, currentPath);
                  return (
                    <button
                      key={item.path}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-3 w-full min-h-11 px-5 py-2.5 text-base font-medium bg-transparent border-none cursor-pointer text-left border-l-3 transition-all duration-[120ms] ${FOCUS_RING} ${
                        active
                          ? 'text-accent border-l-accent bg-[rgba(34,197,94,0.08)]'
                          : 'text-fg-muted border-l-transparent hover:text-fg-primary hover:bg-[rgba(34,197,94,0.04)]'
                      }`}
                      onClick={() => onNavigate(item.path)}
                    >
                      {item.icon && <span className="shrink-0">{item.icon}</span>}
                      {item.label}
                    </button>
                  );
                })}

                {/* Infrastructure section — in global view */}
                {infraSection && (
                  <div className="mt-2">
                    <button
                      onClick={() => setInfraOpen(!infraOpen)}
                      className={`flex items-center gap-2 w-full px-5 py-2.5 bg-transparent border-none text-xs font-semibold text-fg-muted uppercase tracking-wider cursor-pointer hover:text-fg-primary hover:bg-[rgba(34,197,94,0.04)] transition-all duration-[120ms] ${FOCUS_RING}`}
                      aria-expanded={infraOpen}
                      aria-controls="mobile-infra-nav-panel-global"
                    >
                      {infraOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      Infrastructure
                    </button>
                    {infraOpen && (
                      <div id="mobile-infra-nav-panel-global">
                        {infraSection.items.map((item) => {
                          const active = isNavItemActive(item.path, currentPath);
                          return (
                            <button
                              key={item.path}
                              aria-current={active ? 'page' : undefined}
                              className={`flex items-center gap-3 w-full min-h-11 px-5 pl-8 py-2.5 text-base font-medium bg-transparent border-none cursor-pointer text-left border-l-3 transition-all duration-[120ms] ${FOCUS_RING} ${
                                active
                                  ? 'text-accent border-l-accent bg-[rgba(34,197,94,0.08)]'
                                  : 'text-fg-muted border-l-transparent hover:text-fg-primary hover:bg-[rgba(34,197,94,0.04)]'
                              }`}
                              onClick={() => onNavigate(item.path)}
                            >
                              {item.icon && <span className="shrink-0">{item.icon}</span>}
                              {item.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Project list — in global view within project context */}
                {projectListSection}
              </div>
            )}
          </div>
        </nav>

        {/* Sign out */}
        <div className="border-t border-border-default py-2">
          <button
            onClick={onSignOut}
            className={`flex items-center gap-3 w-full min-h-11 px-5 py-2.5 text-base font-medium bg-transparent border-none cursor-pointer text-left border-l-3 border-l-transparent text-danger-fg hover:bg-[rgba(34,197,94,0.04)] transition-all duration-[120ms] ${FOCUS_RING}`}
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
